/**
 * THE review-flag vocabulary — one union, one classification table, one
 * source of truth (issues #845 / #844 / #843 / #786, plan Task 5).
 *
 * Before this module there were TWO vocabularies that had drifted apart:
 *
 * - `reviewArtifact`'s `ReviewFlag` union (per-artifact judge) —
 *   `title-*`, `character-drift`, `object-drift`, `border-mismatch`,
 *   `white-mat`, `other`.
 * - `reviewContinuity`'s `CONTINUITY_FINDING_FLAGS` (cross-page judge) —
 *   `wardrobe-drift`, `prop-drift`, `background-drift`, `character-drift`,
 *   `duplicate-character`, `text-image-mismatch`, `other`.
 *
 * The two judges therefore had DISJOINT BLIND SPOTS: the cross-page judge
 * could name a text↔image disagreement, the per-page judge could only call it
 * `other`; the per-page judge could name a border defect, the cross-page judge
 * could only call it `other`. Merging the vocabulary is what makes a defect
 * class routable, gradeable and dedupable no matter which judge saw it.
 *
 * ## `object-drift` vs `prop-drift` — BOTH slugs are kept, deliberately
 *
 * They are the same concept under two names. Neither is canonicalised away:
 *
 * - `prop-drift` is already written into `orders.continuity_findings` JSONB
 *   rows in production and into committed eval fixtures / manifest
 *   expectations. Rewriting it would either invalidate stored audit records or
 *   force a data migration on an untyped column for no functional gain.
 * - `object-drift` is already written into the per-page and cover review
 *   PROMPTS, which this task may not reword.
 *
 * So both are members of the union and both classify IDENTICALLY (see
 * {@link REVIEW_FLAG_CLASSIFICATION}). {@link canonicalReviewFlag} maps
 * `prop-drift → object-drift` for any caller that needs a single key to
 * deduplicate on — it is a READ-side normalisation only and must never be used
 * to rewrite a stored or wire value.
 *
 * ## Repair OWNERSHIP (plan Task 16, decision D2)
 *
 * The table also records WHICH LAYER may spend money repairing each defect
 * class — the in-loop page reviewer, the post-graph continuity sweep, or
 * (for the identity-drift slugs) whichever one the finding's own evidence
 * belongs to. That is `repairOwner` + {@link isInLoopOwnedFinding}; it lives
 * here, in the one classification table, rather than as a parallel list in
 * the sweep, for exactly the reason the rest of the table does: a new slug
 * must not compile until someone has decided how it routes.
 *
 * ## Wire posture
 *
 * The wire stays permissive everywhere (`z.string()`, never `z.enum`) per the
 * repo's DB/wire schema rule. This union is narrowed AFTER parsing by
 * {@link isReviewFlag}; anything unrecognised coerces to `'other'` via
 * {@link coerceReviewFlag} so a defect signal is preserved rather than
 * silently dropped.
 */

/**
 * Every flag any reviewer may emit, in prompt-documentation order:
 * cover-only title checks, the two cover-only jacket checks, then the
 * drift family, then the standalone page checks, then the catch-all.
 *
 * ⚠️ **Membership here is NOT the same as being taught to the per-artifact
 * judge.** `ARTIFACT_REVIEW_OUTPUT_FORMAT_BLOCK` documents ten of these
 * slugs; `prop-drift`, `wardrobe-drift`, `background-drift`,
 * `duplicate-character`, `cover-spine-band`, `cover-fold-subject`,
 * `cover-hero-absent` and `premature-cast` are the EIGHT members it never
 * names, because their emitters are the cross-page judge and four focused
 * passes with their own
 * structured verdict channels. The standing
 * block has a
 * FIXED attention budget (#854), so a slug goes into it only when a judge
 * reading it is asked to emit it.
 *
 * 🚨 **THREE `title-*` slugs survive, and `title-bleed` is still not one of
 * them (#878 Task 8, extended #881).**
 *
 *  - `title-mismatch` / `title-illegible` / `title-clipped` — KEPT, and all
 *    three are reachable. The one-shot cover spread's own review pass emits
 *    them from its transcription verdict: `title-mismatch` on a spelling
 *    disagreement, `title-illegible` on a low-contrast one, `title-clipped`
 *    on lettering the panel edge cuts through
 *    (`skills/reviewCoverSpread.ts`).
 *  - `title-bleed` — **RETIRED.** It only ever routed one way: it set
 *    `useDeterministicTitleOverlay` on the §9.1 fallback, and #878 Task 5
 *    made that lever INERT (the title is painted into the artwork by the
 *    image model, so there is nothing to switch to). A slug that nothing
 *    routes on is a slug that spends judge attention teaching a decision
 *    the code cannot take. Removing it was a typecheck event that cascaded
 *    into `ARTIFACT_REVIEW_OUTPUT_FORMAT_BLOCK`, the cover-review prompt
 *    builders and their zero-headroom length pins — which is exactly why
 *    it was deferred out of the pure-removal commit and taken here, where
 *    every affected budget is re-pinned DOWNWARD in the same change.
 *    ⚠️ **The reason first given for retiring it was WRONG, and the
 *    correction is why `title-clipped` exists.** That reasoning ran: a
 *    title cropped at the trim needs no slug, because "a clipped glyph
 *    changes the transcription" and the comparison would catch it. It does
 *    not. Measured on the 16-cover probe of 2026-08-30: an English
 *    hardcover shipped with its title running off the right edge —
 *    «Moon Boat» rendered as «Moor Boa…» — and the transcription pass
 *    reported CLEAN, writing that "contrast is good and each letter is
 *    clearly distinguishable". A vision model reads a partly-drawn word as
 *    the word it plainly is; that is good OCR and the wrong instrument.
 *    `renderedTitleMatches` compares CONTENT and structurally cannot see
 *    CONTAINMENT, so the defect had no detector at all. `title-clipped`
 *    restores one by asking the judge the geometric question directly.
 *    It is NOT a revival of `title-bleed`: that slug's only routing was the
 *    inert deterministic-overlay lever, whereas this one re-rolls the
 *    spread through the existing title budget.
 *
 * 🚨 **`premature-cast` (#918) is a CAST-COMPOSITION slug, not a drift slug.**
 *
 * On order `6fd3f4ac` the fox cub was drawn on page 1 while the plan gave it
 * `firstAppearancePageId: "page-4"` and page 4's own text narrates the first
 * meeting. Every existing slug missed it, and each for a structural reason:
 * the fox matched the character sheet perfectly (`character-drift` no), it
 * appeared once (`duplicate-character` no), and page 1's text simply never
 * mentions it, which contradicts nothing (`text-image-mismatch` no). A
 * `mustShow` assertion CANNOT catch an extra character — a picture cannot
 * depict absence, which is precisely why `VisualContract.mustNotShow` exists.
 *
 * It is also the missing counter-pressure. `dropped-companion` pushes
 * companions to be MORE present — on this very book the plan judge instructed
 * the planner to "explicitly show the маленький лисёнок" on seven pages. There
 * was no flag on the other side of that. Now there is.
 *
 * Emitted ONLY by `skills/reviewPrematureCast.ts`, a focused pass. It is
 * deliberately NOT taught to `pageReviewPrompt.ts`: #854 measured that
 * prompt's attention budget as fixed, and appending a block moved a new defect
 * 0/5 → 1/5 while costing an existing catch 3/5 → 1/5.
 */
export const REVIEW_FLAGS = [
  'title-illegible',
  'title-mismatch',
  'title-clipped',
  'cover-band-text',
  'cover-spine-band',
  'cover-fold-subject',
  'cover-hero-absent',
  'character-drift',
  'object-drift',
  'prop-drift',
  'wardrobe-drift',
  'background-drift',
  'border-mismatch',
  'white-mat',
  'duplicate-character',
  'premature-cast',
  'text-image-mismatch',
  'physics-implausible',
  'other',
] as const;

/** The unified review-flag union. */
export type ReviewFlag = (typeof REVIEW_FLAGS)[number];

/**
 * The artifact surfaces a flag can be scoped to.
 *
 * `'page'` covers page composites. `'cover'` covers all three cover stages
 * (front, outpaint wrap, 3D mockup). The character sheet is judged by the same
 * reviewer but is deliberately NOT a scope of its own — no flag is
 * CS-exclusive, and `white-mat` is explicitly excluded from the CS (its wash
 * interior is by design, see `apps/server/CLAUDE.md`).
 */
export type ReviewFlagScope = 'page' | 'cover' | 'any';

/**
 * WHICH LAYER is authoritative for REPAIRING this defect class (plan
 * Task 16, decision D2).
 *
 * Two layers can spend money on an image defect, and each defect should be
 * owned by exactly ONE of them so budget is never spent twice:
 *
 * - **in-loop** — the per-page reviewer inside `generatePagesNode`, running
 *   DURING page generation. It is authoritative for what it can see from one
 *   artifact plus the approved character sheet, it regenerates a page for
 *   roughly a tenth the cost of a post-hoc targeted edit, and it is the only
 *   layer that can iterate BEFORE the page is bound into the book. Budget:
 *   `MAX_REGENS_PER_ARTIFACT`.
 * - **sweep** — the post-graph `continuitySweep`, running after every page
 *   exists. It is authoritative for cross-page and canon defects: the things
 *   ONE page cannot reveal. Budget: `CONTINUITY_MAX_REGENS_PER_BOOK`.
 * - **both** — 🚨 the honest answer for the identity-drift slugs. Ownership
 *   there depends on what the finding is RELATIVE TO, not on the slug:
 *   `character-drift` means "this page disagrees with the character sheet"
 *   (the in-loop reviewer has the CS attached and already judged exactly
 *   that) OR "page 14 disagrees with page 15" (only the sweep can see it).
 *   A slug-level split would misclassify half the cases either way, so the
 *   table records `'both'` and the RUNTIME discriminator decides — see
 *   {@link isInLoopOwnedFinding}. The discriminator is available and cheap:
 *   a continuity finding naming ONE page is the own-page question, a finding
 *   naming several is the cross-page one.
 * - **unowned** — `'other'` only. It has NO defect identity (two consecutive
 *   `['other']` reviews are usually two different complaints), so "the other
 *   layer already handled this one" is not a statement that can be made
 *   about it. Never skipped, never deduped across layers.
 */
export type ReviewFlagRepairOwner = 'in-loop' | 'sweep' | 'both' | 'unowned';

/** How one flag is classified for routing, scoping and grading. */
export interface ReviewFlagClassification {
  /**
   * Which artifact surfaces this flag may legitimately apply to.
   *
   * The scoping rules this table encodes, unchanged from before the
   * merge except for the last (#816):
   * - `white-mat` is PAGE ONLY (covers are borderless; the CS wash is
   *   by-design).
   * - `border-mismatch` NEVER applies to covers (borderless by design).
   * - the two `title-*` slugs are COVER ONLY.
   * - `cover-band-text` is COVER ONLY — the reserved outpaint bands it
   *   describes exist only on the wrap canvas.
   * - `cover-spine-band` is COVER ONLY — a page has no spine and no fold.
   * - `cover-fold-subject` is COVER ONLY — a page has no fold for a face to
   *   land on (#889).
   */
  scope: ReviewFlagScope;
  /**
   * VISUAL-PERSISTENCE class: the artifact drifted from an APPROVED
   * REFERENCE (the character sheet, its prop row, its border, or an anchor
   * page). This is a statement about the defect's NATURE, not about how it
   * routes — see {@link isTypedReviewFlag} for the routing/record predicate.
   */
  persistence: boolean;
  /**
   * The cover subgraph FORCE-REGENERATES the front cover on this flag even
   * when the supernode accepted (#766), bounded by the per-artifact cap.
   * Only the identity-drift slugs qualify.
   */
  coverForcesRegen: boolean;
  /**
   * Part of the cross-page continuity judge's DOCUMENTED vocabulary. The
   * continuity wire is permissive, so this only decides what the eval grader
   * counts as "documented" versus "the judge invented a slug".
   */
  continuity: boolean;
  /**
   * Assumption A4 — a PAGE-review flag can NEVER convert a paid book into a
   * terminal failure at the global regen cap. The page force-accepts, records
   * the flag and ships. Typed as the literal `false` so the table cannot
   * express the opposite without an explicit, reviewed type change.
   */
  hardFailsAtGlobalCap: false;
  /**
   * Which repair layer owns this defect class (plan Task 16 / decision D2).
   * See {@link ReviewFlagRepairOwner} for what each value means and
   * {@link isInLoopOwnedFinding} for how `'both'` is resolved at runtime.
   *
   * A flag the per-artifact reviewer's prompt does not teach can only ever
   * reach us from the CROSS-PAGE judge, so classifying it `'sweep'` is
   * lossless — the in-loop layer cannot emit it and therefore can never have
   * force-accepted it.
   */
  repairOwner: ReviewFlagRepairOwner;
}

const COVER_TITLE: ReviewFlagClassification = {
  scope: 'cover',
  persistence: false,
  coverForcesRegen: false,
  continuity: false,
  hardFailsAtGlobalCap: false,
  // The cover subgraph's own review loop owns the title, and the sweep has
  // no title repair at all — its only cover tool re-rolls the whole spread
  // and it is never handed a title slug.
  repairOwner: 'in-loop',
};

const CROSS_PAGE_DRIFT: ReviewFlagClassification = {
  scope: 'any',
  persistence: true,
  coverForcesRegen: false,
  continuity: true,
  hardFailsAtGlobalCap: false,
  // Defined as a change BETWEEN pages (or, for `duplicate-character`, a
  // repetition across the wrap's two panels). None of the three is in the
  // per-artifact reviewer's taught vocabulary, so the in-loop layer cannot
  // name them even when it sees them — they arrive as `other` there.
  repairOwner: 'sweep',
};

const PAGE_ONLY_DRIFT: ReviewFlagClassification = {
  scope: 'page',
  persistence: true,
  coverForcesRegen: false,
  continuity: false,
  hardFailsAtGlobalCap: false,
  // Border treatment and mats are judged against the approved character
  // sheet on the page's OWN surface — the in-loop reviewer's question
  // exactly. Neither is in the cross-page judge's vocabulary either, so the
  // sweep structurally cannot allocate a repair for them.
  repairOwner: 'in-loop',
};

/**
 * The classification table. EXHAUSTIVE by type — a new member of
 * {@link REVIEW_FLAGS} does not compile until it has been classified for
 * routing, which is the point (a defect class with no identity cannot be
 * routed, graded or deduped).
 */
export const REVIEW_FLAG_CLASSIFICATION: Readonly<
  Record<ReviewFlag, ReviewFlagClassification>
> = {
  // The #688 §1 ground-truth title check, re-homed on the one-shot
  // spread's transcription pass. Cover-only by construction — pages carry
  // no title. ⚠️ See the note on {@link REVIEW_FLAGS} for why these three
  // survive and `title-bleed` did not.
  'title-illegible': COVER_TITLE,
  'title-mismatch': COVER_TITLE,

  // A title CUT OFF by the panel edge. It shares `COVER_TITLE` because it is
  // the same family — a defect of the lettering on the front cut, emitted by
  // the same focused pass, repaired by the same re-roll — and the constant's
  // every field is right for it unchanged: cover-only (a page has no title),
  // no drift from an approved reference (the title was never a reference),
  // no cross-page question, in-loop ownership.
  //
  // 🚨 It is a SEPARATE slug from `title-mismatch` because the two are
  // separate repairs and separate rates. A clipped title is spelled
  // correctly and reads clearly; only its geometry is wrong, so the regen
  // hint that fixes it ("keep the whole title inside the panel") is not the
  // hint that fixes a misspelling. Folding it into `title-mismatch` would
  // also have hidden the measurement that produced it — see the note on
  // {@link REVIEW_FLAGS}.
  'title-clipped': COVER_TITLE,

  // #816 — GHOST TITLE TEXT IN THE OUTPAINT RESERVED BANDS.
  //
  // On a hardcover wrap the outpaint model regenerates the whole canvas,
  // title included, at its own scale; `restampFrontCover` then pastes the
  // ORIGINAL front cover back over the front-cover slot. Everything the
  // model drew INSIDE the slot is overwritten, everything it drew in the
  // reserved bands AROUND it survives — a truncated second title above and
  // to the right of the real one.
  //
  // Deliberately NOT reusing {@link COVER_TITLE}: that constant is the
  // §9.1 deterministic-overlay family, and this flag triggers no overlay
  // fallback. Written out so every field is an explicit decision:
  //
  // - `scope: 'cover'` — a reserved band exists only on the wrap canvas.
  // - `persistence: false` — nothing drifted from an approved reference.
  //   The reserved band is a region NO approved reference covers at all: the
  //   character sheet, its prop row and the approved front cover all describe
  //   the panel, never the margin around it. The defect is NEW lettering in
  //   pixels that had no reference to drift from.
  //   ⚠️ The earlier wording justified this with "the front cover is
  //   byte-identical after the re-stamp". That is no longer true — #876
  //   turned the re-stamp OFF, so the front face on the wrap is the model's
  //   own redraw. The classification is unaffected because it never depended
  //   on the re-stamp; the sentence was an illustration, not the reason.
  // - 🚨 `coverForcesRegen: false` — the force path this field feeds
  //   (`COVER_PERSISTENCE_REVIEW_FLAGS`, additionally filtered on
  //   `persistence` in `subagents/cover/graph.ts`) is scoped to identity
  //   drift against the character sheet and re-rolls the FRONT cover. That
  //   is the wrong repair here: the front cover is clean and only the
  //   outpaint needs re-rolling. A DEDICATED outpaint force predicate owns
  //   this flag instead.
  // - `continuity: false` — a single-artifact check on one canvas; the
  //   cross-page judge is never asked for it and its prompt never teaches
  //   the slug.
  // - `repairOwner: 'in-loop'` — RE-READ AFTER #876, and still correct, but
  //   for a narrower reason than it used to be. Ownership records WHICH
  //   LAYER MAY SPEND on a defect class, not that the layer will succeed.
  //   The cover subgraph's own review loop is the only layer that may: the
  //   sweep's single cover tool re-rolls the wrap but cannot be handed a
  //   slug outside its documented vocabulary, and `'unowned'` is reserved
  //   for `'other'`, which has no defect identity at all. So `'in-loop'`
  //   stays — and `isInLoopOwnedFinding` keeps the sweep off it, which is
  //   the behaviour that matters.
  //   ⚠️ #872 gave the loop a deterministic ENDING (three re-rolls on a
  //   dedicated `BAND_TEXT_MAX_REGENS` counter, then a reserved-band repair
  //   that replaced the band with artwork copied from inside the approved
  //   slot). 🚨 **#876 REMOVED that repair**: it produced visibly wrong
  //   covers on real books, and #876's title-less outpaint base plus the
  //   enumerated NO-TEXT-ANYWHERE rule removed the geometric mechanism that
  //   made the ghost in the first place.
  //   🚨 So state the ending plainly, because it is weaker than #872's and a
  //   stale doc here would describe a guarantee the code no longer makes:
  //   the RE-ROLL LOOP still terminates (the budget binds in both
  //   directions — `shouldForceBandTextRegen` bounds what the band asks for,
  //   `shouldStopOutpaintRerollsForBandText` stops the other re-roll paths
  //   once it is spent), but NOTHING REPAIRS THE BAND. Past the budget the
  //   cover SHIPS with the ghost, and the only guarantee is that it does not
  //   ship silently: the slug stays on the outpaint envelope
  //   (`outpaintFlags`), `orders.cover_band_text_shipped_at` is stamped so
  //   the rate is queryable, and the Telegram cover-preview message carries
  //   one advisory line. It is never a hard failure (#729).
  'cover-band-text': {
    scope: 'cover',
    persistence: false,
    coverForcesRegen: false,
    continuity: false,
    hardFailsAtGlobalCap: false,
    repairOwner: 'in-loop',
  },

  // A DRAWN SPINE on the one-shot cover spread.
  //
  // The image model draws the whole jacket in one call and the server cuts
  // both canonical artifacts out of it by derived geometry. Live order
  // `11f20073-88b3-424e-b4d3-cd9da9609358` drew an ornate patterned vertical
  // band centred at x≈702 of the 1536 px spread against a fold at x=768 —
  // 66 px, i.e. 0.60 in on the printed jacket, so the decoration prints well
  // inside the BACK COVER. There is no wording that makes a drawn spine land
  // on the fold, so the generation prompt forbids one outright
  // (`NO_DRAWN_SPINE_BLOCK`) and this slug names the residual.
  //
  // 🚨 Deliberately NOT reusing `cover-band-text`. That slug means LETTERING
  // in the reserved margin outside the front-cover panel; it carries an
  // ENFORCEMENT half (`shouldStopCoverRerollsForBandText`) that stops OTHER
  // re-roll paths once its budget is spent, which is the right response to a
  // defect measured NOT to converge on a re-roll and the wrong one here. A
  // drawn spine is a fresh composition decision every draw. Same reasoning as
  // #816's: reusing a slug means inheriting its routing.
  //
  // Written out so every field is an explicit decision:
  //
  // - `scope: 'cover'` — a page has no spine and no fold.
  // - `persistence: false` — nothing DRIFTED from an approved reference. No
  //   approved reference describes the middle of the jacket at all; the
  //   defect is new furniture in pixels that had nothing to drift from.
  // - `coverForcesRegen: false` — the force path this field feeds
  //   (`COVER_PERSISTENCE_REVIEW_FLAGS`, additionally filtered on
  //   `persistence`) is scoped to identity drift against the character sheet.
  //   A DEDICATED force predicate owns this flag, bounded by its own re-roll
  //   budget AND by the SHARED lifetime spread image-call cap.
  // - `continuity: false` — a single-artifact check on one sheet; the
  //   cross-page judge is never asked for it and its prompt never teaches the
  //   slug.
  // - `repairOwner: 'in-loop'` — the cover subgraph's own review loop is the
  //   only layer that may spend on it. The sweep's single cover tool re-rolls
  //   the wrap but cannot be handed a slug outside its documented vocabulary,
  //   and `'unowned'` is reserved for `'other'`.
  //
  // 🚨 The ending, stated plainly: the re-roll loop TERMINATES and past the
  // budget the cover SHIPS with the band, never hard-fails (#729). What the
  // shipped finding buys is visibility — the slug on `spreadFlags` plus a
  // durable log line carrying the orderId.
  'cover-spine-band': {
    scope: 'cover',
    persistence: false,
    coverForcesRegen: false,
    continuity: false,
    hardFailsAtGlobalCap: false,
    repairOwner: 'in-loop',
  },

  // A FACE or primary subject sitting on the printed spine crease (#889).
  //
  // 3 of 30 covers in the 2026-08-25 probe run: #3 the grandmother, #5 a
  // creature's head, #16 a scientist — faces where the jacket physically folds.
  //
  // 🚨 This does NOT contradict the recorded "character lost at the fold-side
  // cut: 12/12 -> 0/12" result. That measured the CUT (does the front cover
  // CONTAIN the character). This measures the FOLD (does the crease run THROUGH
  // a face on the printed jacket). Different boundary, one axis over.
  //
  // 🚨 Deliberately NOT reusing `cover-spine-band`. That slug means the model
  // DREW a spine that is not on the fold; this one means real artwork landed on
  // a fold that is exactly where it belongs. Reusing a slug means inheriting its
  // routing, and these two want separate rates.
  //
  // - `scope: 'cover'` — a page has no fold.
  // - `persistence: false` — nothing DRIFTED from an approved reference; the
  //   subject is exactly the one the character sheet approved, in the wrong
  //   place on the sheet.
  // - `coverForcesRegen: false` — a DEDICATED force predicate owns this,
  //   bounded by its own re-roll budget AND the SHARED lifetime spread
  //   image-call cap.
  // - `continuity: false` — a single-artifact check on one sheet; the
  //   cross-page judge is never asked for it and its prompt never teaches the
  //   slug.
  // - `repairOwner: 'in-loop'` — the cover subgraph's own review loop is the
  //   only layer that may spend on it.
  // - `hardFailsAtGlobalCap: false` — past the budget the cover SHIPS with the
  //   face on the crease and the slug stays on `spreadFlags` (#729).
  'cover-fold-subject': {
    scope: 'cover',
    persistence: false,
    coverForcesRegen: false,
    continuity: false,
    hardFailsAtGlobalCap: false,
    repairOwner: 'in-loop',
  },

  // The PROTAGONIST is not on the front cover at all (psss order `18010c38`,
  // 2026-10-01): attempt 6 of 6 drew the hero on the back panel only, the
  // artwork judge said so in prose ("the approved protagonist Eitan is
  // entirely absent") — and filed it as `other`, which no record keeps, so
  // the server logged `cover verdict: accepted clean` and shipped it.
  //
  // Emitted ONLY by `cover/reviews.ts`'s `reviewCoverHeroPresence`, a
  // focused pass with one yes/no question. 🚨 Deliberately NOT taught to the
  // artwork prompt — #854: a new defect class gets a new focused pass, never
  // another block in an existing prompt.
  //
  // - `scope: 'cover'` — a page has its own cast rules (`premature-cast`,
  //   `mustShow`); "the hero is on the front" is a question about the jacket.
  // - `persistence: false` — nothing DRIFTED from a reference; the hero is
  //   simply not there to compare.
  // - `coverForcesRegen: false` — that field feeds the #766 identity-drift
  //   path. A DEDICATED force in `cover/workflow.ts` owns this slug, bounded
  //   by its own `HERO_ABSENT_MAX_REGENS` (2, `cover/budgets.ts`) and the
  //   SHARED spread image-call cap — never charged as a detector — so the
  //   identical-flag breaker cannot stop it re-rolling a repeated absence
  //   while both have room.
  // - `continuity: false` — a single-artifact check on one cut.
  // - `repairOwner: 'in-loop'` — the cover's own review loop is the only
  //   layer that may spend on it.
  'cover-hero-absent': {
    scope: 'cover',
    persistence: false,
    coverForcesRegen: false,
    continuity: false,
    hardFailsAtGlobalCap: false,
    repairOwner: 'in-loop',
  },

  // Identity drift vs the approved character sheet / its prop row. These are
  // the only slugs that force a cover regen (#766).
  //
  // `object-drift` and `prop-drift` agree on every ROUTING dimension —
  // they are one defect class. They differ only in `continuity`, which is
  // not a routing property: it records WHICH PROMPT teaches the slug, and
  // the cross-page prompt teaches `prop-drift` exclusively. Flipping
  // `object-drift` to `continuity: true` would make the eval grader start
  // counting it as a documented cross-page finding, changing grading
  // semantics for a slug that judge is never asked to emit.
  //
  // All three are `repairOwner: 'both'` — the genuinely ambiguous case
  // Task 16 refuses to force into a slug-level split. "This page disagrees
  // with the approved character sheet" is the in-loop reviewer's own
  // question (the CS is attached to every page review); "page 14 disagrees
  // with page 15" is a question only the sweep can ask. Same slug, two
  // different defects, resolved by {@link isInLoopOwnedFinding} from what
  // the finding is relative to.
  'character-drift': {
    scope: 'any',
    persistence: true,
    coverForcesRegen: true,
    continuity: true,
    hardFailsAtGlobalCap: false,
    repairOwner: 'both',
  },
  'object-drift': {
    scope: 'any',
    persistence: true,
    coverForcesRegen: true,
    continuity: false,
    hardFailsAtGlobalCap: false,
    repairOwner: 'both',
  },
  /** Alias of `object-drift` — see the module docstring. */
  'prop-drift': {
    scope: 'any',
    persistence: true,
    coverForcesRegen: true,
    continuity: true,
    hardFailsAtGlobalCap: false,
    repairOwner: 'both',
  },

  // Drift the CROSS-PAGE judge names. Real persistence defects, but a single
  // cover re-roll is not the repair for them.
  'wardrobe-drift': CROSS_PAGE_DRIFT,
  'background-drift': CROSS_PAGE_DRIFT,
  'duplicate-character': CROSS_PAGE_DRIFT,

  // #918 — a cast member rendered on a page that excludes them.
  //
  // Written out rather than sharing a constant, because no existing constant
  // is right: `CROSS_PAGE_DRIFT` is `persistence: true` + `repairOwner:
  // 'sweep'`, and both are wrong here.
  //
  // - `scope: 'page'` — the exclusion is authored per page. A cover has no
  //   per-page cast list to violate.
  // - `persistence: false` — NOTHING drifted. The character is drawn correctly
  //   and matches the approved character sheet exactly; the defect is that it
  //   is on the page at all. Marking it a persistence flag would route it into
  //   the drift machinery, which compares against a reference and would find
  //   no fault.
  // - `continuity: false` — checkable from ONE page plus its own exclusion
  //   list, so the cross-page judge would only duplicate the question.
  // - `repairOwner: 'in-loop'` — the focused pass runs inside
  //   `generatePagesNode` alongside the anatomy pass, so the page can be
  //   redrawn for roughly a tenth of a post-hoc targeted edit, and BEFORE the
  //   page is bound into the book.
  'premature-cast': {
    scope: 'page',
    persistence: false,
    coverForcesRegen: false,
    continuity: false,
    hardFailsAtGlobalCap: false,
    repairOwner: 'in-loop',
  },

  // Page-surface-only checks. The cover is borderless by design, so neither
  // can apply there.
  'border-mismatch': PAGE_ONLY_DRIFT,
  'white-mat': PAGE_ONLY_DRIFT,

  // Semantic defects — the picture disagrees with the prose, or with physics.
  // NOT persistence: nothing drifted from a reference, the render is simply
  // wrong about the world. They still carry a defect IDENTITY, which is what
  // makes them routable (see `isTypedReviewFlag`).
  'text-image-mismatch': {
    scope: 'any',
    persistence: false,
    coverForcesRegen: false,
    continuity: true,
    hardFailsAtGlobalCap: false,
    // BOTH, for the same reason as the drift family and with the same
    // runtime resolution. "Page 5's picture contradicts page 5's text" is
    // the own-page contract check the in-loop reviewer runs (it has the
    // page's own text and its authored visual contract); a finding naming
    // SEVERAL pages is a claim about the pages disagreeing with each other,
    // which one page cannot reveal.
    repairOwner: 'both',
  },
  'physics-implausible': {
    scope: 'any',
    persistence: false,
    coverForcesRegen: false,
    // Cross-page judging never asked for physics; Task 7 adds it to the
    // per-artifact reviewer only.
    continuity: false,
    hardFailsAtGlobalCap: false,
    // A sealed flask that pours is wrong on its own page, in one frame.
    repairOwner: 'in-loop',
  },

  // The catch-all. NO defect identity — the specific complaint lives in the
  // reviewer's prose, which differs attempt to attempt.
  other: {
    scope: 'any',
    persistence: false,
    coverForcesRegen: false,
    continuity: true,
    hardFailsAtGlobalCap: false,
    // UNOWNED, deliberately. Ownership is a claim that the OTHER layer
    // already dealt with THIS defect; `other` names no defect, so the claim
    // cannot be made. Two `['other']` reports are usually two unrelated
    // complaints — the same reason `isTypedReviewFlag` excludes it from the
    // #826 loop breaker and from the durable accept record.
    repairOwner: 'unowned',
  },
};

const REVIEW_FLAG_SET = new Set<string>(REVIEW_FLAGS);

/** Typeguard narrowing an arbitrary (wire) string to {@link ReviewFlag}. */
export function isReviewFlag(value: string): value is ReviewFlag {
  return REVIEW_FLAG_SET.has(value);
}

/**
 * Narrow a wire string to the union, coercing anything unrecognised to
 * `'other'`.
 *
 * Coercion rather than dropping is load-bearing: the page routing layer
 * accepts an artifact whenever `flags.length === 0`, so a dropped unknown slug
 * would silently ship an artifact whose `observations` describe a real defect.
 */
export function coerceReviewFlag(value: string): ReviewFlag {
  return isReviewFlag(value) ? value : 'other';
}

/**
 * A judge's raw flag strings as typed {@link ReviewFlag}s: empty strings
 * dropped, every other value narrowed by {@link coerceReviewFlag}, then
 * deduplicated (first occurrence wins the order).
 */
export function normalizeReviewFlags(raw: readonly string[]): ReviewFlag[] {
  return Array.from(
    new Set(
      raw
        .filter((flag) => flag.length > 0)
        .map((flag) => coerceReviewFlag(flag))
    )
  );
}

/**
 * True when the flag names a SPECIFIC defect class — i.e. anything but the
 * `'other'` catch-all.
 *
 * This is the predicate that gates the durable record (`acceptedFlags` + the
 * `'persistence flag accepted at cap'` log line) and the #826 identical-flag
 * loop breaker. It is deliberately "has an identity", NOT "is a persistence
 * flag": two consecutive `['other']` reviews are usually two DIFFERENT
 * complaints, so set-equality carries no signal there and a force-accept would
 * be both a guess and SILENT; every other slug names one thing and is worth
 * recording.
 */
export function isTypedReviewFlag(flag: ReviewFlag): boolean {
  return flag !== 'other';
}

/** Filter to the typed (non-`other`) subset. Returns a NEW array. */
export function filterTypedReviewFlags(
  flags: readonly ReviewFlag[]
): ReviewFlag[] {
  return flags.filter(isTypedReviewFlag);
}

function flagsWhere(
  predicate: (classification: ReviewFlagClassification) => boolean
): readonly ReviewFlag[] {
  return REVIEW_FLAGS.filter((flag) =>
    predicate(REVIEW_FLAG_CLASSIFICATION[flag])
  );
}

/**
 * The VISUAL-PERSISTENCE subset (#766, #796) — drift against an approved
 * reference.
 *
 * Derived from {@link REVIEW_FLAG_CLASSIFICATION} so the table stays the only
 * place a flag is classified.
 */
export const PERSISTENCE_REVIEW_FLAGS: readonly ReviewFlag[] = flagsWhere(
  (c) => c.persistence
);

/**
 * The subset that FORCES a front-cover regen when the character-sheet anchor
 * was attached to that review (#766) — identity drift only. `border-mismatch`
 * / `white-mat` are excluded because the cover is intentionally borderless.
 */
export const COVER_PERSISTENCE_REVIEW_FLAGS: readonly ReviewFlag[] = flagsWhere(
  (c) => c.coverForcesRegen
);

/** The cross-page judge's documented vocabulary. */
export const CONTINUITY_REVIEW_FLAGS: readonly ReviewFlag[] = flagsWhere(
  (c) => c.continuity
);

/**
 * Flags the IN-LOOP page/cover reviewer is authoritative for, unconditionally.
 * See {@link ReviewFlagRepairOwner}.
 */
export const IN_LOOP_OWNED_REVIEW_FLAGS: readonly ReviewFlag[] = flagsWhere(
  (c) => c.repairOwner === 'in-loop'
);

/**
 * Flags the POST-GRAPH continuity sweep is authoritative for,
 * unconditionally. See {@link ReviewFlagRepairOwner}.
 */
export const SWEEP_OWNED_REVIEW_FLAGS: readonly ReviewFlag[] = flagsWhere(
  (c) => c.repairOwner === 'sweep'
);

/**
 * Flags whose owner depends on what the finding is RELATIVE TO — resolved by
 * {@link isInLoopOwnedFinding}, never by the slug alone.
 */
export const DUAL_OWNED_REVIEW_FLAGS: readonly ReviewFlag[] = flagsWhere(
  (c) => c.repairOwner === 'both'
);

/**
 * Does the IN-LOOP page reviewer own this particular FINDING?
 *
 * The only input beyond the slug is `crossPage` — whether the finding
 * compares TWO OR MORE artifacts. That is the whole of the runtime
 * discrimination, and it is exactly the question the in-loop reviewer
 * structurally cannot answer: it sees one artifact plus the approved
 * character sheet, so a claim about two pages disagreeing is outside its
 * evidence no matter which slug carries it.
 *
 * - `'in-loop'` → always true. The sweep has no repair for these anyway
 *   (none is in the cross-page judge's documented vocabulary), so this is a
 *   statement of authority rather than a live routing decision.
 * - `'both'` → true only for a SINGLE-artifact finding. A multi-page finding
 *   is the sweep's, even though the slug is one the in-loop layer can emit.
 * - `'sweep'` / `'unowned'` → always false.
 *
 * Callers use this to decide whether the sweep may SPEND on a finding the
 * in-loop layer already force-accepted at its cap. It is deliberately not a
 * decision about whether to RECORD the finding: a residual record costs
 * nothing and is the audit trail.
 */
export function isInLoopOwnedFinding(
  flag: ReviewFlag,
  options: { crossPage: boolean }
): boolean {
  const { repairOwner } = REVIEW_FLAG_CLASSIFICATION[flag];
  if (repairOwner === 'in-loop') return true;
  if (repairOwner === 'both') return !options.crossPage;
  return false;
}

/** Flags that apply ONLY to page composites (never covers). */
export const PAGE_ONLY_REVIEW_FLAGS: readonly ReviewFlag[] = flagsWhere(
  (c) => c.scope === 'page'
);

/** Flags that apply ONLY to cover artifacts (never pages). */
export const COVER_ONLY_REVIEW_FLAGS: readonly ReviewFlag[] = flagsWhere(
  (c) => c.scope === 'cover'
);

/** Filter `flags` to the visual-persistence subset. Returns a NEW array. */
export function filterPersistenceFlags(
  flags: readonly ReviewFlag[]
): ReviewFlag[] {
  return flags.filter((flag) => REVIEW_FLAG_CLASSIFICATION[flag].persistence);
}

/** Filter `flags` to the cover-force-regen subset. Returns a NEW array. */
export function filterCoverPersistenceFlags(
  flags: readonly ReviewFlag[]
): ReviewFlag[] {
  return flags.filter(
    (flag) => REVIEW_FLAG_CLASSIFICATION[flag].coverForcesRegen
  );
}

/**
 * True when `flag` is in scope for the given artifact surface.
 *
 * `'page'` composites reject the cover-only `title-*` slugs; cover artifacts
 * reject the page-only `border-mismatch` / `white-mat`.
 */
export function isReviewFlagInScope(
  flag: ReviewFlag,
  surface: 'page' | 'cover'
): boolean {
  const { scope } = REVIEW_FLAG_CLASSIFICATION[flag];
  return scope === 'any' || scope === surface;
}

/**
 * Collapse the `prop-drift` alias onto `object-drift` for READ-side keying
 * (dedup, cross-window merging, grading buckets).
 *
 * Never use this to rewrite a stored `orders.continuity_findings` row or a
 * wire payload — production rows and committed eval fixtures carry
 * `prop-drift` verbatim and must keep round-tripping.
 */
export function canonicalReviewFlag(flag: ReviewFlag): ReviewFlag {
  return flag === 'prop-drift' ? 'object-drift' : flag;
}
